import React, {useEffect} from 'react';
import MainApp from '@/modules/MainApp';
import {useAuth, useUser} from '@dofleini/security';
import UnauthenticatedApp from '@/modules/UnauthenticatedApp';
import {authentication} from '@/settings/authentication';
import OnboardingModule from '@/modules/onboarding';
import PageLoader from '@/components/PageLoader';
import {useTermsGate} from '@/modules/terms/hooks/useTermsGate';
import TermsModal from '@/modules/terms/components/TermsModal';
import {logout as fullLogout} from '@/modules/authentication/services/AuthClient';
import {AuditReportInternationalizationProvider} from '@/contexts/AuditReportInternationalizationContext';
import {message} from 'antd';
import {useTranslation} from 'react-i18next';
import {useLocation, useParams} from 'react-router-dom';
import ShareAuditReport from '@/modules/ShareAuditReport';
import {i18n} from '@/settings';
import {ApiClientService} from '@dofleini/security';
import {useMutation, useQueryClient,} from 'react-query';
import TokenService from '@/contexts/TokenService';
import {getSimpleLocale, useInternationalization} from '@/contexts/InternationalizationContext';
import { initDB, useIndexedDB } from 'react-indexed-db-hook';
import {DBConfig} from '@/constants/indexedDbConfig';
import {useRunAuditOnlineStatus} from '@/contexts/RunAuditOnlineStatusProvider';
import RunAuditSyncService from '@/services/RunAuditSyncService';
import useAlerts from '@/hooks/useAlerts';
import SyncLayer from '@/components/SyncLayer';
import {Blob} from 'formdata-node';
import AuditorAuditsApiService from '@/modules/auditorAudits/services/AuditorAuditsApiService';
import * as Sentry from '@sentry/react';

initDB(DBConfig);

ApiClientService.setup({
  TokenService,
  loginPath: /(\/api\/signin)|(\/api\/refresh-token)|(\/api\/signin-webapp)|(\/api\/social-signin-webapp)/
});

ApiClientService.interceptors.request.use(async (config) => {
  if (config.ignoreToken) return config;
  const pending = TokenService.isRefreshing;
  if (pending) {
    await pending.catch(() => null);
  } else if (TokenService.isTokenExpired()) {
    await TokenService.refresh().catch(() => null);
  }
  return config;
});

if (!Sentry.getCurrentHub().getClient()) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.REACT_APP_SENTRY_ENV || 'development',
    enabled: process.env.REACT_APP_SENTRY_ENABLED || false,
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    // Tracing
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    // tracePropagationTargets: ['localhost', /^https:\/\/yourserver\.io\/api/],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });
}

function App() {
  const {user} = useUser();
  const {t} = useTranslation('common');
  const {logout} = useAuth();
  const {pathname} = useLocation();
  const { id } = useParams();
  const {setLocale} = useInternationalization();
  const { add, getAll, deleteRecord } = useIndexedDB('queries');
  const {setOnline, online, setSynchronizing, synchronizing, setAuditOnline, setRunAuditReload} = useRunAuditOnlineStatus();
  const {putErrorCode} = useAlerts();
  const queryClient = useQueryClient();
  const termsGate = useTermsGate(user);

  useEffect(() => {
    setRunAuditReload(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {mutateAsync: syncAudit} = useMutation(RunAuditSyncService.syncAudits,
    {
      onError: (e) => {
        online && putErrorCode(e);
      }
    }
  );

  const {mutateAsync: checkScore} = useMutation(AuditorAuditsApiService.checkScore,
    {
      onError: (e) => {
        online && putErrorCode(e);
        setSynchronizing(false);
      }
    }
  );

  // useLocationChange(async (location, prevLocation) => {
  //   if (prevLocation?.pathname?.includes('/client-registers/run-register') && !location?.pathname?.includes('/client-registers/run-register')) {
  //     const splitDir = prevLocation?.pathname?.split('/');
  //     const register = splitDir[splitDir?.length - 1];
  //     setRegister(register);
  //     setIsCheckingPending(true);
  //     await checkIsPending({register});
  //   }
  // });

  useEffect(() => {
    window.addEventListener('online', () => {
      if (pathname?.includes('auditor/audits/run')) {
        // window.location.reload();
        setAuditOnline(true);
        setRunAuditReload(false);
      }
      setOnline(true);
    });
    window.addEventListener('offline', () => {
      setOnline(false);
      if (pathname?.includes('auditor/audits/run')) {
        setAuditOnline(false);
        setRunAuditReload(false);
      }
    });
  }, [pathname, setAuditOnline, setOnline, setRunAuditReload]);

  useEffect(() => {
    ApiClientService.interceptors.request.use(function (config) {
      config.headers['client-date'] = new Date().toISOString();
      config.headers['x-timezone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return config;
    }, function (error) {
      return Promise.reject(error);
    });
  }, []);

  useEffect(() => {
    if (online && user) {
      getAll().then(async (queries) => {
        if (queries && queries?.length > 0) {
          try {
            await TokenService.refresh();
          } catch (e) {
            putErrorCode(e);
            setSynchronizing(false);
            return;
          }
          setAuditOnline(true);
          setSynchronizing(true);
          const keysToDelete = [];
          const userQueries = queries?.filter((query) => query?.user === user?._id);
          if ((!userQueries || userQueries.length === 0) && queries.length > 0) {
            const deletePromises = queries?.map(async ({id: key}) => {
              await deleteRecord(key).catch((e) => {
                putErrorCode(e);
                setSynchronizing(false);
              });
            });
            await Promise.all(deletePromises);
            setSynchronizing(false);
            setRunAuditReload(false);
            return;
          }
          const auditsToCheck = [];
          for (const configDB of userQueries) {
            const config = JSON.parse(configDB?.config);
            let {data} = config;
            const {url, method, isBlob, blobType, headers, audit} = config;
            if (audit && auditsToCheck.indexOf(audit) === -1) {
              auditsToCheck.push(audit);
            }
            if (isBlob) {
              data = new Blob([data], {type: blobType});
            }
            await syncAudit({url, data, method, isBlob, headers}).catch(async (e) => {
              putErrorCode(e);
              await deleteRecord(configDB?.id);
              setSynchronizing(false);
            });
            keysToDelete.push(configDB?.id);
          }
          const deletePromises = keysToDelete?.map(async (key) => {
            await deleteRecord(key).catch((e) => {
              putErrorCode(e);
              setSynchronizing(false);
            });
          });
          await Promise.all(deletePromises);
          if (auditsToCheck?.length > 0) {
            for (const audit of auditsToCheck) {
              await checkScore({audit});
            }
          }
          setSynchronizing(false);
          setRunAuditReload(true);
        }
      });
    }
  }, [checkScore, deleteRecord, getAll, id, online, pathname, putErrorCode, queryClient, setAuditOnline, setRunAuditReload, setSynchronizing, syncAudit, user, user?._id]);

  useEffect(() => {
    ApiClientService.interceptors.response.use(
      (response) => {
        return response;
      },
      async (error) => {
        if (error?.response?.message === 'Network Error'
            && pathname?.includes('auditor/audits/run')
            && error?.response?.config?.method !== 'get'
            && !error?.response?.config?.url?.includes('questions-answers')
            && !error?.response?.config?.url?.includes('/search')
        ) {
          if (error?.response?.config) {
            error.response.config.user = user?._id;
            error.response.config.audit = pathname?.split('/').pop();
          }
          if (error?.response?.config?.data instanceof Blob) {
            const blobText = await error?.response?.config?.data.text();
            error.response.config.isBlob = true;
            error.response.config.blobType = error?.response?.config?.data?.type;
            error.response.config.data = blobText;
          }
          const config = JSON.stringify(error?.response?.config);
          console.log('API ERROR:: ', error);
          console.log('API ERROR CONFIG:: ', config);
          add({config, user: user?._id})
            .then(
              (event) => {
                console.log('===ID Generated: ', event);
              },
              (error) => {
                console.log(error);
              },
            );
        }
        return Promise.reject(error);
      }
    );
  }, [pathname, add, setOnline, user?._id, id]);

  if (synchronizing) {
    return (
      <SyncLayer />
    );
  }

  if (pathname?.includes('share-audit-report')) return (
    <AuditReportInternationalizationProvider>
      <ShareAuditReport/>
    </AuditReportInternationalizationProvider>
  );

  if (user && authentication?.activeOnboarding && !user?.onboardingCompleted && user.isAuditor) {
    message.config({maxCount: 1});
    message.error(t('auditorOnBoardingError'));
    window.localStorage.clear();
    ApiClientService.setSpace('');
    logout();
    setTimeout(() => {
      window.location.reload();
    }, 2000);
    return;
  }

  // Apply the user's language before any authenticated UI (terms modal, onboarding),
  // which otherwise would render in the default language since the `if (user)` block
  // below runs after these early returns.
  if (user) {
    i18n.changeLanguage(user?.language);
    setLocale(user?.language);
  }

  // External users (isRoot === false) must accept the active Terms & Conditions
  // before anything else -- onboarding included. Blocking modal, no way around it.
  if (termsGate.loading) return <PageLoader/>;
  if (termsGate.needsAcceptance) return (
    <TermsModal
      terms={termsGate.terms}
      onAccepted={termsGate.refetch}
      onReject={async () => {
        // Full logout: clears both the localStorage and sessionStorage access tokens
        // (change-space/impersonate stores it in sessionStorage) + space.
        await fullLogout();
        logout();
        // Redirect to login instead of reloading: /change-space also exists as an
        // unauthenticated route (an infinite PageLoader), so reloading there hangs.
        window.location.href = '/signin';
      }}
      refetch={termsGate.refetch}
    />
  );

  if (user && authentication?.activeOnboarding && !user?.onboardingCompleted && !user.isAuditor)
    return <OnboardingModule/>;

  if (user) {
    i18n.changeLanguage(user?.language);
    setLocale(user?.language);
    return <>
      <MainApp/>
    </>;
  }

  const lang = navigator.language;

  i18n.changeLanguage(getSimpleLocale(lang) || 'es');
  return <UnauthenticatedApp/>;
}

export default App;
